import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { UserCvService } from './user-cv.service';
import { RootCvService } from './root-cv.service';

@Injectable({
  providedIn: 'root',
})
export class UserCvPresenceGuard implements CanActivate {
  constructor(
    private userCvService: UserCvService,
    private rootCvService: RootCvService,
    private router: Router
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    if (state.url.includes('/home/profile')) {
      return of(true);
    }

    if (state.url.includes('/home/user-cvs')) {
      return of(true);
    }

    // ATS Scan (REQ-CV-031) is a standalone analysis tool - it works on
    // any uploaded PDF and never reads the user's Root CV/Profile, so it
    // has no business being gated on having one yet. Same reasoning as
    // the /home/user-cvs bypass above.
    if (state.url.includes('/home/ats-scan')) {
      return of(true);
    }

    // recent-jobs (POC) - a third-party job listing, no matching against
    // the user's Root CV yet. Same reasoning as the ats-scan bypass above:
    // this screen never reads Root CV/Profile content, so it has no
    // business being gated on having one.
    if (state.url.includes('/home/recent-jobs')) {
      return of(true);
    }

    // Covers /home/root-cv, /home/root-cv/start, /home/root-cv/:language and
    // /home/root-cv/:language/review - the Root CV screens must stay
    // reachable for a brand-new account (chamba-ai#268: Root CV is now the
    // first thing a new user fills in), which is exactly the population
    // this guard would otherwise redirect away from them.
    if (state.url.includes('/home/root-cv')) {
      return of(true);
    }

    // Diagnostic session, 2026-08-20: this used to also pass when the user
    // had zero Root CVs but at least one legacy User CV, on the theory
    // that "some CV content" was enough for the rest of the app to be
    // useful. Confirmed live as a bug, not a feature - an account that
    // migrated from the legacy flow (has user_cvs, has no Root CV yet)
    // never got redirected here, so it fell through to
    // ProfileCompletionGuard instead, which has no notion of Root CV at
    // all - the user could complete their Profile and land straight on
    // the dashboard, never once seeing the Root CV bootstrap screen.
    // "Does this user need to be sent to create a Root CV" is answered by
    // one fact only: whether they have a Root CV. Legacy User CV history
    // is unrelated to that question, even though it's still fetched below
    // (UserCvService.getUserCVHistory has a side effect other parts of the
    // app may depend on - this.userCVs$ - so the call stays; only the
    // redirect decision itself no longer reads its result).
    // Returns a UrlTree instead of calling this.router.navigate()
    // imperatively - this guard and ProfileCompletionGuard both run
    // concurrently (Angular's canActivate array evaluates every guard on a
    // route in parallel, not sequentially - see prioritizedGuardValue() in
    // @angular/router), so an imperative navigate() call here can lose a
    // race to ProfileCompletionGuard's own navigate() if that one's single
    // HTTP call happens to resolve first (this guard needs two, via
    // forkJoin). Returning a UrlTree lets Angular's own guard-priority
    // logic decide the outcome deterministically based on canActivate
    // array order ([..., UserCvPresenceGuard, ProfileCompletionGuard] on
    // the 'home' route) regardless of which guard's HTTP call finishes
    // first - the same pattern AuthGuard/AdminGuard already use.
    return forkJoin({
      userCvs: this.userCvService.getUserCVHistory(),
      rootCvs: this.rootCvService.listRootCvs(),
    }).pipe(
      map(({ rootCvs }) => {
        const hasRootCv = (rootCvs || []).length > 0;
        return hasRootCv ? true : this.router.createUrlTree(['/home/root-cv/start']);
      }),
      catchError(() => of(this.router.createUrlTree(['/home/root-cv/start'])))
    );
  }
}
